import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { ClientAvatar, refreshClientAvatars } from "@/components/ClientAvatar";
import { runAryeoSync } from "@/lib/sync";
import { PropertyThumb } from "@/components/PropertyThumb";
import { SmartReminders } from "@/components/SmartReminders";
import { DueGroups } from "@/components/DueGroups";
import { DashboardCustomize, DEFAULT_LAYOUT, normalizeLayout } from "@/components/DashboardCustomize";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import TimeSummary from "@/components/TimeSummary";
import ShootsMap from "@/components/ShootsMap";
import { Button } from "@/components/ui/button";
import { DeliverableBadge, PaymentBadge, DeliverableStatusBadge } from "@/components/Badges";
import { PRODUCTION_STATUSES, STATUS_META, formatCurrency, formatDate, formatTime, formatDayLabel, formatDateTime } from "@/lib/constants";
import {
  RefreshCw, DollarSign, ClipboardList, Users, ListChecks, Loader2, ArrowRight, AlertCircle,
  Sun, CalendarClock, Camera, Clock, UserCog, Timer, CheckCircle2, AlertTriangle, LayoutGrid,
} from "lucide-react";
import { toast } from "sonner";

function Connector({ className = "" }) {
  return <span aria-hidden className={`h-px w-3 shrink-0 rounded-full bg-muted-foreground/40 ${className}`} />;
}

function ShootCard({ s, navigate, hidePhotographer }) {
  return (
    <button onClick={() => navigate(`/orders/${s.order_id}`)}
      data-testid={`today-shoot-${s.order_id}`}
      className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-2.5 text-left transition-transform hover:-translate-y-0.5 hover:border-primary/40">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{s.address}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {!hidePhotographer && (
            <span className="inline-flex items-center gap-1.5">
              <Camera className="h-3 w-3 shrink-0" />{s.photographers.join(", ") || "—"}
            </span>
          )}
          {s.client_name && (
            <span className="inline-flex min-w-0 items-center gap-1.5" data-testid={`today-shoot-client-${s.order_id}`}>
              <ClientAvatar name={s.client_name} className="h-4 w-4 text-[7px]" /><span className="truncate">{s.client_name}</span>
            </span>
          )}
        </div>
        {s.products?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1.5" data-testid={`today-shoot-products-${s.order_id}`}>
            {s.products.map((p, i) => (
              <span key={`${s.order_id}-${p.type}-${i}`} className="inline-flex items-center">
                <DeliverableBadge type={p.type} />
                {p.editor_name && (
                  <>
                    <Connector />
                    <span className="editor-bubble inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                      style={{ "--editor-color": p.editor_color }}
                      data-testid={`today-shoot-editor-${s.order_id}-${i}`}>
                      <UserCog className="h-2.5 w-2.5" />{p.editor_name}
                    </span>
                  </>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 self-start whitespace-nowrap text-xs font-medium text-primary">
        <Clock className="h-3 w-3" />{formatTime(s.start_at)}
      </div>
    </button>
  );
}

function groupShootsByPhotographer(shoots) {
  const m = {};
  shoots.forEach((s) => {
    const ps = s.photographers && s.photographers.length ? s.photographers : ["Unassigned"];
    ps.forEach((p) => { if (!m[p]) m[p] = []; m[p].push(s); });
  });
  Object.values(m).forEach((arr) => arr.sort((a, b) => (a.start_at || "").localeCompare(b.start_at || "")));
  return Object.entries(m).sort((a, b) => (a[1][0].start_at || "").localeCompare(b[1][0].start_at || ""));
}

function StatCard({ icon: Icon, label, value, hint, tint }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="overline text-muted-foreground">{label}</span>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${tint}22` }}>
          <Icon className="h-4 w-4" style={{ color: tint }} />
        </div>
      </div>
      <div className="mt-3 font-heading text-3xl font-extrabold tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function Sec({ id, layout, children }) {
  if (!layout || layout.hidden.includes(id)) return null;
  return <div style={{ order: layout.order.indexOf(id) }} data-testid={`dash-section-${id}`}>{children}</div>;
}

export default function Dashboard() {
  const [layout, setLayout] = useState(null);
  const [customizing, setCustomizing] = useState(false);
  const saveTimer = useRef(null);
  useEffect(() => {
    api.get("/me/dashboard-layout").then(({ data }) => setLayout(normalizeLayout(data.layout)))
      .catch(() => setLayout(DEFAULT_LAYOUT));
  }, []);
  const changeLayout = (next) => {
    const n = normalizeLayout(next);
    setLayout(n);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/me/dashboard-layout", { layout: n }).catch(() => toast.error("Couldn't save layout"));
    }, 400);
  };
  const [stats, setStats] = useState(null);
  const [today, setToday] = useState(null);
  const [shootTab, setShootTab] = useState("list");
  const [dueTab, setDueTab] = useState("today");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const { user, checkAuth } = useAuth();
  const [lastSync, setLastSync] = useState(null);
  const [reminders, setReminders] = useState([]);
  const navigate = useNavigate();

  const iso = (d) => d.toISOString().slice(0, 10);
  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
  const [range, setRange] = useState({ start: daysAgo(30), end: iso(new Date()), preset: "30" });

  useEffect(() => { if (user?.last_sync_at) setLastSync(user.last_sync_at); }, [user]);

  const loadStats = async (r) => {
    const { data } = await api.get(`/dashboard/stats?start=${r.start}&end=${r.end}`);
    setStats(data);
  };

  const load = async (r = range) => {
    try {
      const [{ data: t }, { data: rm }] = await Promise.all([
        api.get("/dashboard/today"),
        api.get("/dashboard/reminders"),
        loadStats(r),
      ]);
      setToday(t);
      setReminders(rm.reminders || []);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyPreset = (preset) => {
    let r;
    if (preset === "7") r = { start: daysAgo(7), end: iso(new Date()), preset };
    else if (preset === "30") r = { start: daysAgo(30), end: iso(new Date()), preset };
    else if (preset === "90") r = { start: daysAgo(90), end: iso(new Date()), preset };
    else if (preset === "ytd") r = { start: `${new Date().getFullYear()}-01-01`, end: iso(new Date()), preset };
    else if (preset === "all") r = { start: "2000-01-01", end: iso(new Date()), preset };
    else return;
    setRange(r);
    loadStats(r).catch((err) => toast.error(apiError(err)));
  };

  const applyCustom = (field, value) => {
    const r = { ...range, [field]: value, preset: "custom" };
    setRange(r);
    if (r.start && r.end) loadStats(r).catch((err) => toast.error(apiError(err)));
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const data = await runAryeoSync();
      refreshClientAvatars();
      const extras = [
        data.created ? `${data.created} new` : null,
        data.archived ? `${data.archived} archived` : null,
        data.removed ? `${data.removed} removed` : null,
      ].filter(Boolean).join(", ");
      toast.success(`Synced ${data.total} orders${extras ? ` (${extras})` : ""}`);
      if (data.last_sync_at) setLastSync(data.last_sync_at);
      await load();
      checkAuth();
    } catch (err) {
      toast.error(apiError(err, "Sync failed"));
    } finally {
      setSyncing(false);
    }
  };

  const markDelivered = async (orderId, delivId) => {
    try {
      await api.patch(`/orders/${orderId}/deliverables/${delivId}`, { status: "DELIVERED" });
      toast.success("Marked delivered");
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const reminderDone = async (r) => {
    try {
      if (r.kind === "loaded") {
        await api.patch(`/orders/${r.order_id}/deliverables/${r.deliverable_id}`, { status: "DELIVERED" });
        toast.success(`${r.type} marked done`);
      } else if (r.kind === "start") {
        await api.patch(`/orders/${r.order_id}/deliverables/${r.deliverable_id}`, { status: "EDITING" });
        toast.success(`${r.type} moved to Editing`);
      } else {
        await api.post("/dashboard/reminders/dismiss", { key: r.key });
        toast.success("Reminder hidden");
      }
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (loading) {
    return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const chartData = (stats?.deliverable_breakdown || []).map((d) => ({ name: d.type, count: d.count }));
  const BAR_COLORS = ["#3B82F6", "#A855F7", "#F59E0B", "#10B981", "#EF4444", "#06B6D4", "#8B5CF6", "#F97316"];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Your real estate media operation at a glance" testid="dashboard-header">
        <div className="flex items-center gap-3">
          {lastSync && (
            <span className="hidden text-xs text-muted-foreground sm:inline" data-testid="last-sync-label">
              Last synced {formatDateTime(lastSync)}
            </span>
          )}
          <Button variant="outline" onClick={() => setCustomizing(true)} data-testid="customize-btn" className="gap-2">
            <LayoutGrid className="h-4 w-4" /> Customize
          </Button>
          <Button onClick={sync} disabled={syncing} data-testid="sync-btn" className="gap-2">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync from Aryeo
          </Button>
        </div>
      </PageHeader>

      {layout && <DashboardCustomize open={customizing} onOpenChange={setCustomizing} layout={layout} onChange={changeLayout} />}
      <div className="flex flex-col gap-8 p-6 md:p-8">
        {!stats?.aryeo_connected && (
          <div style={{ order: -1 }} className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="aryeo-warning">
            <AlertCircle className="h-4 w-4" />
            Aryeo isn't connected. Add your API key in Integrations to pull in orders.
          </div>
        )}

        <Sec layout={layout} id="reminders"><SmartReminders reminders={reminders} onDone={reminderDone} onOpen={(r) => navigate(`/orders/${r.order_id}`)} /></Sec>

        <Sec layout={layout} id="today">
        {/* Today */}
        {today && (
          <div className="rounded-xl border border-border bg-card" data-testid="today-panel">
            <div className="flex items-center gap-2 border-b border-border px-5 py-4">
              <Sun className="h-4 w-4 text-amber-400" />
              <span className="overline text-muted-foreground">Today</span>
              <span className="text-sm font-medium">
                {formatDayLabel(today.date)}
              </span>
            </div>
            <div className="grid grid-cols-1 divide-y divide-border md:grid-cols-2 md:divide-x md:divide-y-0">
              {/* Deliverables due & overdue */}
              <div className="p-5">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                  <CalendarClock className="h-4 w-4 text-amber-400" /> Deliverables due
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{(dueTab === "tomorrow" ? (today.tomorrow_due || []) : today.due).length}</span>
                  <div className="ml-auto inline-flex rounded-lg border border-border p-0.5">
                    <button onClick={() => setDueTab("today")} data-testid="due-tab-today"
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${dueTab === "today" ? "bg-secondary text-foreground" : "text-muted-foreground"}`}>Due &amp; Overdue</button>
                    <button onClick={() => setDueTab("tomorrow")} data-testid="due-tab-tomorrow"
                      className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${dueTab === "tomorrow" ? "bg-secondary text-foreground" : "text-muted-foreground"}`}>
                      Tomorrow
                      {(today.tomorrow_due || []).length > 0 && (
                        <span data-testid="due-tab-tomorrow-count"
                          className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-500">
                          {(today.tomorrow_due || []).length}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
                {(dueTab === "tomorrow" ? (today.tomorrow_due || []) : today.due).length === 0 ? (
                  <p className="text-sm text-muted-foreground">{dueTab === "tomorrow" ? "Nothing due tomorrow." : "Nothing due or overdue. 🎉"}</p>
                ) : (
                  <DueGroups items={dueTab === "tomorrow" ? (today.tomorrow_due || []) : today.due}
                    tomorrow={dueTab === "tomorrow"} onOpen={(oid) => navigate(`/orders/${oid}`)} onDeliver={markDelivered} />
                )}
              </div>
              {/* Shoots today */}
              <div className="p-5">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                  <Camera className="h-4 w-4 text-primary" /> Shoots today
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{today.shoots.length}</span>
                  <div className="ml-auto inline-flex rounded-lg border border-border p-0.5">
                    <button onClick={() => setShootTab("list")} data-testid="shoots-tab-list"
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${shootTab === "list" ? "bg-secondary text-foreground" : "text-muted-foreground"}`}>List</button>
                    <button onClick={() => setShootTab("map")} data-testid="shoots-tab-map"
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${shootTab === "map" ? "bg-secondary text-foreground" : "text-muted-foreground"}`}>Map</button>
                    <button onClick={() => setShootTab("group")} data-testid="shoots-tab-group"
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${shootTab === "group" ? "bg-secondary text-foreground" : "text-muted-foreground"}`}>Group</button>
                  </div>
                </div>
                {shootTab === "map" ? (
                  <ShootsMap shoots={today.shoots} team={today.team || []} />
                ) : today.shoots.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No shoots scheduled for today.</p>
                ) : shootTab === "group" ? (
                  <div className="space-y-4" data-testid="shoots-grouped">
                    {groupShootsByPhotographer(today.shoots).map(([photog, list]) => (
                      <div key={photog} data-testid={`shoots-group-${photog}`}>
                        <div className="mb-2 flex items-center gap-2 text-sm font-bold text-foreground">
                          <Camera className="h-4 w-4 text-primary" />{photog}
                          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">{list.length}</span>
                        </div>
                        <div className="ml-2 space-y-2 border-l-2 border-primary/25 pl-4">
                          {list.map((s) => (
                            <ShootCard key={s.order_id + s.start_at} s={s} navigate={navigate} hidePhotographer />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {today.shoots.map((s) => (
                      <ShootCard key={s.order_id + s.start_at} s={s} navigate={navigate} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </Sec>

        <Sec layout={layout} id="business">
        <div className="space-y-4 rounded-2xl border border-border bg-card/40 p-4" data-testid="business-stats-section">
        {/* Business stats date filter */}
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 xl:flex-row xl:items-center xl:justify-between" data-testid="stats-date-filter">
          <div className="flex min-w-0 items-center gap-2 whitespace-nowrap">
            <CalendarClock className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Business stats</span>
            <span className="text-xs text-muted-foreground" data-testid="stats-window-label">
              {stats?.window ? `${formatDayLabel(stats.window.start, { month: "short", day: "numeric" })} – ${formatDayLabel(stats.window.end, { month: "short", day: "numeric" })}` : ""}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { k: "7", label: "7d" }, { k: "30", label: "30d" }, { k: "90", label: "90d" },
              { k: "ytd", label: "YTD" }, { k: "all", label: "All" },
            ].map((p) => (
              <button key={p.k} onClick={() => applyPreset(p.k)} data-testid={`stats-preset-${p.k}`}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${range.preset === p.k ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                {p.label}
              </button>
            ))}
            <div className="flex flex-wrap items-center gap-1.5">
              <input type="date" value={range.start} max={range.end} onChange={(e) => applyCustom("start", e.target.value)}
                data-testid="stats-start-input"
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground" />
              <span className="text-xs text-muted-foreground">to</span>
              <input type="date" value={range.end} min={range.start} max={iso(new Date())} onChange={(e) => applyCustom("end", e.target.value)}
                data-testid="stats-end-input"
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground" />
            </div>
          </div>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={ClipboardList} label="Total Orders" value={stats.total_orders} tint="#3B82F6"
            hint={`${stats.products_total || 0} products tracked`} />
          <StatCard icon={DollarSign} label="Revenue" value={formatCurrency(stats.revenue_cents)} tint="#10B981"
            hint={`${formatCurrency(stats.unpaid_cents)} unpaid`} />
          <StatCard icon={ListChecks} label="Deliverables Due" value={stats.deliverables_pending} tint="#F59E0B"
            hint="Not yet completed" />
          <StatCard icon={Users} label="Clients" value={stats.clients_count} tint="#A855F7"
            hint="Active agents" />
        </div>

        {/* Pipeline + chart */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-5 lg:col-span-1" data-testid="pipeline-summary">
            <span className="overline text-muted-foreground">Product Pipeline</span>
            <div className="mt-4 space-y-3">
              {PRODUCTION_STATUSES.map((s) => {
                const counts = stats.product_status_counts || {};
                const total = stats.products_total || 0;
                const count = counts[s] || 0;
                const pct = total ? (count / total) * 100 : 0;
                return (
                  <div key={s}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[s].accent }} />
                        {STATUS_META[s].label}
                      </span>
                      <span className="font-mono text-muted-foreground">{count}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: STATUS_META[s].accent }} />
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center justify-between border-t border-border pt-2 text-xs text-muted-foreground">
                <span>Total products tracked</span>
                <span className="font-mono">{stats.products_total || 0}</span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2" data-testid="deliverable-chart">
            <div className="flex items-center justify-between">
              <span className="overline text-muted-foreground">Deliverable Mix</span>
              <span className="text-xs text-muted-foreground">{chartData.reduce((s, d) => s + d.count, 0)} total</span>
            </div>
            {chartData.length === 0 ? (
              <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">No data yet</div>
            ) : (
              <div className="mt-5 space-y-3.5">
                {[...chartData].sort((a, b) => b.count - a.count).map((entry, i) => {
                  const total = chartData.reduce((s, d) => s + d.count, 0) || 1;
                  const max = Math.max(...chartData.map((d) => d.count)) || 1;
                  const share = Math.round((entry.count / total) * 100);
                  const color = BAR_COLORS[i % BAR_COLORS.length];
                  return (
                    <div key={entry.name} data-testid={`mix-row-${entry.name.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                      <div className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
                        <span className="truncate font-medium">{entry.name}</span>
                        <span className="shrink-0 font-mono text-xs text-muted-foreground">
                          <span className="text-foreground">{entry.count}</span> · {share}%
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-secondary">
                        <div className="h-full rounded-full transition-all" style={{ width: `${(entry.count / max) * 100}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        </div>
        </Sec>

        <Sec layout={layout} id="ontime">
        {/* On-time delivery */}
        {(() => {
          const ot = stats.on_time || { on_time: 0, late: 0, measured: 0, rate: null, by_type: [] };
          const rateColor = ot.rate == null ? "#94A3B8" : ot.rate >= 90 ? "#10B981" : ot.rate >= 70 ? "#F59E0B" : "#EF4444";
          return (
            <div className="rounded-xl border border-border bg-card" data-testid="ontime-panel">
              <div className="flex items-center gap-2 border-b border-border px-5 py-4">
                <Timer className="h-4 w-4 text-emerald-400" />
                <span className="overline text-muted-foreground">On-Time Delivery</span>
                <span className="ml-auto text-xs text-muted-foreground">{ot.window_days ? `Last ${ot.window_days} days` : "Selected range"} · {ot.measured} measured</span>
              </div>
              {ot.measured === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="ontime-empty">
                  No delivery data yet. As you mark tracked products <span className="font-medium text-foreground">Delivered</span>, we'll compare each one to its due date and show your on-time rate here.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 p-5 md:grid-cols-3">
                  {/* Rate */}
                  <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-secondary/20 p-5 text-center" data-testid="ontime-rate">
                    <div className="font-heading text-5xl font-extrabold tracking-tight" style={{ color: rateColor }}>{ot.rate}%</div>
                    <div className="mt-1 text-xs text-muted-foreground">on-time rate</div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1.5 text-emerald-400" data-testid="ontime-count"><CheckCircle2 className="h-4 w-4" />{ot.on_time} on time</span>
                      <span className="flex items-center gap-1.5 text-rose-400" data-testid="ontime-late-count"><AlertTriangle className="h-4 w-4" />{ot.late} late</span>
                    </div>
                  </div>
                  {/* By type */}
                  <div className="md:col-span-2">
                    <div className="mb-3 text-xs font-medium text-muted-foreground">By product type</div>
                    <div className="space-y-2.5">
                      {ot.by_type.map((row) => {
                        const pct = row.total ? Math.round((row.on_time / row.total) * 100) : 0;
                        return (
                          <div key={row.type} data-testid={`ontime-type-${row.type.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                            <div className="mb-1 flex items-center justify-between text-sm">
                              <span className="font-medium">{row.type}</span>
                              <span className="font-mono text-xs text-muted-foreground">{row.on_time}/{row.total} · {pct}%</span>
                            </div>
                            <div className="flex h-1.5 overflow-hidden rounded-full bg-secondary">
                              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                              <div className="h-full bg-rose-500 transition-all" style={{ width: `${100 - pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        </Sec>

        <Sec layout={layout} id="time"><TimeSummary testid="dashboard-time-summary" compact /></Sec>

        <Sec layout={layout} id="recent">
        {/* Recent orders */}
        <div className="rounded-xl border border-border bg-card" data-testid="recent-orders">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <span className="overline text-muted-foreground">Recent Orders</span>
            <button onClick={() => navigate("/orders")} className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80">
              View all <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="divide-y divide-border">
            {stats.recent_orders.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                No orders yet. Hit "Sync from Aryeo" to pull them in.
              </div>
            ) : (
              stats.recent_orders.map((o) => (
                <div key={o.id} onClick={() => navigate(`/orders/${o.id}`)}
                  className="flex cursor-pointer items-center justify-between px-5 py-3 transition-colors hover:bg-secondary/40"
                  data-testid={`recent-order-${o.id}`}>
                  <PropertyThumb orderId={o.id} show={o.thumb_cached} className="mr-3 h-10 w-14" testId={`recent-order-thumb-${o.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{o.address || o.title}</div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{o.client_name && <ClientAvatar name={o.client_name} email={o.client_email} className="h-4 w-4 text-[7px]" />}<span className="truncate">{o.client_name} · {formatDate(o.aryeo_created_at)}</span></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <PaymentBadge status={o.payment_status} />
                    <span className="font-mono text-sm">{formatCurrency(o.total_amount, o.currency)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        </Sec>
      </div>
    </div>
  );
}
